import { useMemo, useState } from 'react';
import { PrismBanner, PrismButton } from '@prism-ui/react';
import { toast } from '../../../Toaster/toast';
import AdminPane from '../../AdminPane';
import { ApplicationRetention } from './ApplicationRetention/ApplicationRetention';
import { LoggingRetention } from './LoggingRetention/LoggingRetention';
import { ProjectRetention } from './ProjectRetention/ProjectRetention';

type RetentionTab = 'application' | 'logging' | 'project';

const tabContent: Record<
  RetentionTab,
  { title: string; description: string; label: string; Panel: typeof ApplicationRetention }
> = {
  application: {
    title: 'User Applications',
    description: 'Permanently delete expired applications and all application-dependent records.',
    label: 'Application',
    Panel: ApplicationRetention,
  },
  logging: {
    title: 'Audit Logging',
    description: 'Permanently delete expired administrator interaction logs.',
    label: 'Logging',
    Panel: LoggingRetention,
  },
  project: {
    title: 'User Projects',
    description: 'Retain project records while removing configured personal and sensitive fields.',
    label: 'Project',
    Panel: ProjectRetention,
  },
};

const tabOrder: RetentionTab[] = ['application', 'logging', 'project'];

export function DataRetention() {
  const [activeTab, setActiveTab] = useState<RetentionTab>('application');
  // Tabs are mounted on first visit and then kept mounted (hidden when inactive).
  // Unmounting on every switch made each tab re-issue its settings GET on the way
  // back, so switching tabs round-tripped the server for data that had not changed.
  const [mountedTabs, setMountedTabs] = useState<RetentionTab[]>(['application']);

  const selectTab = (tab: RetentionTab) => {
    setActiveTab(tab);
    setMountedTabs((previous) => (previous.includes(tab) ? previous : [...previous, tab]));
  };

  // The panels keep the original `setSuccessMessage`/`setErrorMessage` callback shape;
  // only the presentation moves from an in-page banner to the shared toaster.
  const noticeProps = useMemo(
    () => ({
      setSuccessMessage: (message: string) => {
        if (message) toast.success(message);
      },
      setErrorMessage: (message: string) => {
        if (message) toast.error(message);
      },
    }),
    [],
  );
  const activeContent = tabContent[activeTab];

  return (
    <AdminPane width="narrow" className="clr:flex clr:flex-col clr:gap-md">
      <PrismBanner tone="info" variant="notification">
        <div className="clr:flex clr:flex-col clr:gap-3xs">
          <strong>{activeContent.title}</strong>
          <span>{activeContent.description}</span>
        </div>
      </PrismBanner>

      <div className="clr:grid clr:grid-cols-3 clr:gap-sm" role="tablist" aria-label="Data retention policies">
        {tabOrder.map((tab) => (
          <PrismButton
            key={tab}
            id={`retention-tab-${tab}`}
            tone={activeTab === tab ? 'primary' : 'secondary'}
            display={activeTab === tab ? 'fill' : 'outline'}
            setAriaPressed={activeTab === tab}
            onPrismClick={() => selectTab(tab)}
          >
            {tabContent[tab].label}
          </PrismButton>
        ))}
      </div>

      {tabOrder
        .filter((tab) => mountedTabs.includes(tab))
        .map((tab) => {
          const { Panel } = tabContent[tab];
          const isActive = tab === activeTab;
          return (
            <div
              key={tab}
              className="clr:w-full"
              role="tabpanel"
              id={`retention-panel-${tab}`}
              aria-labelledby={`retention-tab-${tab}`}
              hidden={!isActive}
            >
              <Panel {...noticeProps} active={isActive} />
            </div>
          );
        })}
    </AdminPane>
  );
}
